import { Alert, Box, Button, Paper, Stack, Typography } from '@mui/material';
import React, { useState } from 'react';
import type { DealValues, RegistryHouseDefaults } from '../../api/registryDealTypes';
import { blockGridColumns } from '../../domain/setup/fieldTypes';
import { ALL_FIELDS } from '../../domain/setup/sections';
import { hasRegistryPermission } from '../../session/RegistrySession';
import { PageHeader, SectionCard } from '../../ui/primitives';
import { isHexColour } from '../setup/ColourFieldInput';
import { BrandingPreview } from '../setup/SetupPreviews';
import SetupFieldInput from '../setup/SetupFieldInput';
import DefaultsSideNav from './DefaultsSideNav';
import LogoDropZone from './LogoDropZone';
import {
  asDefaultField,
  type DefaultsSection,
  isShown,
  LIVE_DEFAULT_SECTIONS,
} from './liveDefaultSections';
import { useHouseDefaults } from './useHouseDefaults';
import { useUnsavedChangesWarning } from './useUnsavedChangesWarning';

function previewValues(draft: DealValues, saved: RegistryHouseDefaults): DealValues {
  const colour = (key: string) => {
    const value = String(draft[key] ?? '').trim();
    return isHexColour(value) ? value : '';
  };
  return {
    ...draft,
    'brand.colour': colour('brand.colour'),
    'brand.emailHeader': colour('brand.emailHeader'),
    'brand.emailButton': colour('brand.emailButton'),
    'brand.logoUrl': saved.logoFile ?? '',
    'fund.name': 'Your trust',
  };
}

function SectionFields({
  section,
  defaults,
  editable,
}: {
  section: DefaultsSection;
  defaults: ReturnType<typeof useHouseDefaults>;
  editable: boolean;
}) {
  const { saved, draft, errors, busy, update, uploadLogo } = defaults;
  return (
    <Stack spacing={2.5}>
      {section.blocks.map((block) => (
        <React.Fragment key={block.title ?? section.id}>
          {section.id === 'branding' && block.title === 'Colours' && (
            <LogoDropZone
              logoFile={saved!.logoFile}
              error={defaults.logoError}
              busy={busy}
              editable={editable}
              onUpload={uploadLogo}
            />
          )}
          <Box>
            {block.title && (
              <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                {block.title}
              </Typography>
            )}
            <Box
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: blockGridColumns(block.columns),
              }}
            >
              {block.keys.map((key) => {
                const field = ALL_FIELDS.find((item) => item.key === key);
                if (!field || !isShown(key, draft)) return null;
                return (
                  <React.Fragment key={key}>
                    <SetupFieldInput
                      field={asDefaultField(field)}
                      value={draft[key]}
                      prefill={undefined}
                      error={errors[key]}
                      readOnly={!editable}
                      onChange={(value) => update(key, value)}
                    />
                  </React.Fragment>
                );
              })}
            </Box>
          </Box>
        </React.Fragment>
      ))}
    </Stack>
  );
}

function SaveBar({
  defaults,
  editable,
}: {
  defaults: ReturnType<typeof useHouseDefaults>;
  editable: boolean;
}) {
  const { changed, errors, busy, saveError, save, discard } = defaults;
  const toFix = Object.keys(errors).length;
  const status = toFix
    ? `${toFix} value${toFix === 1 ? '' : 's'} to fix before saving`
    : changed.length
      ? `${changed.length} unsaved change${changed.length === 1 ? '' : 's'}`
      : 'All changes saved';
  return (
    <Paper variant="outlined" sx={{ mt: 2, px: 2.5, py: 1.5 }}>
      {saveError && (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          Defaults not saved: {saveError}
        </Alert>
      )}
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
        <Typography
          variant="body2"
          role="status"
          color={toFix ? 'error' : 'text.secondary'}
          sx={{ flexGrow: 1 }}
        >
          {status}
        </Typography>
        <Button variant="outlined" disabled={busy || !changed.length} onClick={discard}>
          Discard changes
        </Button>
        <Button
          variant="contained"
          disabled={busy || !changed.length || toFix > 0 || !editable}
          onClick={save}
        >
          {busy ? 'Saving…' : 'Save'}
        </Button>
      </Stack>
    </Paper>
  );
}

export default function LiveHouseDefaultsPage() {
  const defaults = useHouseDefaults();
  const { session, saved, draft, loadError } = defaults;
  const [current, setCurrent] = useState(LIVE_DEFAULT_SECTIONS[0].id);
  const editable = hasRegistryPermission(session, 'RegistryAdmin');
  useUnsavedChangesWarning(defaults.changed.length > 0);
  const section = LIVE_DEFAULT_SECTIONS.find((item) => item.id === current)!;
  return (
    <>
      <PageHeader title="Default Values" />
      {loadError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {loadError}
        </Alert>
      )}
      {!saved && !loadError && <Typography>Loading defaults…</Typography>}
      {saved && (
        <>
          {!editable && (
            <Alert severity="info" sx={{ mb: 2 }}>
              You can view these defaults. Changing them needs the Registry admin permission.
            </Alert>
          )}
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              alignItems: 'start',
              gridTemplateColumns: { xs: '1fr', lg: '240px minmax(0, 1fr)' },
            }}
          >
            <DefaultsSideNav
              sections={LIVE_DEFAULT_SECTIONS}
              current={current}
              draft={draft}
              changed={defaults.changed}
              errors={defaults.errors}
              onSelect={setCurrent}
            />
            <Box sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  display: 'grid',
                  gap: 2,
                  alignItems: 'start',
                  gridTemplateColumns: {
                    xs: '1fr',
                    xl: current === 'branding' ? 'minmax(0, 1fr) 340px' : '1fr',
                  },
                }}
              >
                <SectionCard title={section.title} subtitle={section.description}>
                  <SectionFields section={section} defaults={defaults} editable={editable} />
                </SectionCard>
                {current === 'branding' && (
                  <Box>
                    <Typography variant="overline" color="text.secondary">
                      Live login and email preview
                    </Typography>
                    <BrandingPreview values={previewValues(draft, saved)} />
                  </Box>
                )}
              </Box>
              <SaveBar defaults={defaults} editable={editable} />
            </Box>
          </Box>
        </>
      )}
    </>
  );
}
